import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

export const Heritage = () => {
  const { t } = useLang();
  const h = t.heritage;
  return (
  <section className="relative overflow-hidden py-24 lg:py-32" data-testid="heritage-section">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-10">
      <div className="relative lg:col-span-6">
        <Reveal className="img-zoom clip-blob relative aspect-[5/4] overflow-hidden bg-[var(--ink)]">
          <img src={IMG.pepperPick} alt="Hand-picked colored bell peppers" className="h-full w-full object-cover" />
        </Reveal>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="absolute -top-8 left-4 rounded-3xl bg-white p-4 shadow-[0_30px_60px_-30px_rgba(14,61,34,0.45)] lg:-left-8 lg:p-5"
          data-testid="heritage-logo-card"
        >
          <img src="/logo.png" alt="Sebastian's Produce logo" className="h-28 w-auto lg:h-36" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
          whileInView={{ opacity: 1, scale: 1, rotate: -8 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3, type: "spring" }}
          className="absolute -bottom-8 right-4 flex h-36 w-36 flex-col items-center justify-center rounded-full bg-[var(--gold)] text-center text-[var(--ink)] shadow-xl lg:-right-6 lg:h-44 lg:w-44"
          data-testid="heritage-badge"
        >
          <span className="font-mono-label text-[9px]">{h.badgeTop}</span>
          <span className="font-display text-4xl font-bold leading-none lg:text-5xl">1968</span>
          <span className="font-mono-label text-[9px]">{h.badgeBottom}</span>
        </motion.div>
      </div>

      <div className="lg:col-span-6 lg:pl-8">
        <Reveal>
          <p className="eyebrow">{h.eyebrow}</p>
          <h2 className="font-display mt-4 text-3xl font-bold leading-[1.02] sm:text-4xl lg:text-5xl">
            {h.title1} <span className="font-serif-accent font-normal italic text-[var(--green)]">{h.accent}</span> {h.title2}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-7 text-base leading-relaxed text-[var(--ink-2)] md:text-lg">{h.p1}</p>
          <p className="mt-5 text-base leading-relaxed text-[var(--ink-2)] md:text-lg">{h.p2}</p>
        </Reveal>
        <Reveal delay={0.2} className="mt-10 grid grid-cols-2 gap-6 border-t border-[var(--line)] pt-8">
          {h.facts.map(([hd, s]) => (
            <div key={hd}>
              <p className="font-semibold">{hd}</p>
              <p className="mt-1 font-mono-label text-[10px] text-[var(--muted)]">{s}</p>
            </div>
          ))}
        </Reveal>
        <Reveal delay={0.3} className="mt-10">
          <Link to="/about" className="btn-ink" data-testid="heritage-cta-about">
            {h.cta} <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
  );
};
